import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, HardDrive, RefreshCw, Shield, Store, Users } from 'lucide-react';
import axiosInstance from '../../services/axiosInstance';
import { useToast } from '../../contexts/ToastContext';

// Statistiche d'uso della verifica richiamo Takata.
// Una serie per grafico e sempre il numero in chiaro accanto alla barra: le barre
// aiutano l'occhio, i numeri restano la fonte (leggibili anche senza colori).

interface Conteggio { _id: string; n: number }
interface StudioTop { studio: string; provincia?: string; n: number; ultima: string }
interface Stats {
  giorni: number;
  totale: number;
  vinUnici: number;
  studi: number;
  perGiorno: Conteggio[];
  perEsito: Conteggio[];
  perModalita: Conteggio[];
  perMarca: Conteggio[];
  topStudi: StudioTop[];
  disco: { byte: number; file: number };
}

const ESITO_LABEL: Record<string, string> = {
  non_coinvolto: 'Non coinvolto',
  altri_richiami: 'Altri richiami (non Takata)',
  richiamo: 'Coinvolto (richiamo)',
  stop_drive: 'Coinvolto — Stop Drive',
  non_soggetto: 'Marca non soggetta',
  fonte_non_disponibile: 'Impossibile verificare',
};
// Stessi colori dei badge del registro associati: il colore segue l'esito, ovunque.
const ESITO_DOT: Record<string, string> = {
  non_coinvolto: 'bg-blue-500',
  altri_richiami: 'bg-sky-400',
  richiamo: 'bg-red-500',
  stop_drive: 'bg-red-800',
  non_soggetto: 'bg-gray-400',
  fonte_non_disponibile: 'bg-amber-500',
};
const MODALITA_LABEL: Record<string, string> = {
  estensione: 'Estensione (portale della casa)',
  automatica: 'Automatica (lato server)',
  guidata_manuale: 'Guidata manuale',
};

function fmtByte(byte: number) {
  if (byte >= 1024 ** 3) return `${(byte / 1024 ** 3).toFixed(1)} GB`;
  if (byte >= 1024 ** 2) return `${Math.round(byte / 1024 ** 2)} MB`;
  return `${Math.round(byte / 1024)} KB`;
}
const fmtData = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' });

const Tile: React.FC<{ icona: React.ReactNode; valore: string; etichetta: string; nota?: string }> = ({ icona, valore, etichetta, nota }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
    <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 mb-1">{icona}
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{etichetta}</span>
    </div>
    <p className="text-2xl font-bold text-gray-900 dark:text-white">{valore}</p>
    {nota && <p className="text-2xs text-gray-500 dark:text-gray-400 mt-0.5">{nota}</p>}
  </div>
);

// Riga con barra: la larghezza è proporzionale al massimo della lista, il numero è
// sempre stampato — la barra è un aiuto, mai l'unica lettura.
const RigaBarra: React.FC<{ etichetta: string; n: number; max: number; colore?: string; dot?: string }> = ({ etichetta, n, max, colore = 'bg-indigo-500', dot }) => (
  <div className="flex items-center gap-2 py-0.5">
    <span className="w-36 flex-shrink-0 text-xs text-gray-600 dark:text-gray-300 truncate flex items-center gap-1.5">
      {dot && <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />}
      {etichetta}
    </span>
    <div className="flex-1 h-3.5 bg-gray-100 dark:bg-gray-700/60 rounded overflow-hidden">
      <div className={`h-full rounded ${colore}`} style={{ width: `${max ? Math.max((n / max) * 100, 1) : 0}%` }} />
    </div>
    <span className="w-14 flex-shrink-0 text-right text-xs font-semibold text-gray-900 dark:text-white tabular-nums">{n.toLocaleString('it-IT')}</span>
  </div>
);

const TakataStatsAdmin: React.FC = () => {
  const toast = useToast();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/admin/takata-stats');
      if (data.success) setStats(data);
    } catch (e) {
      toast.error('Non riesco a caricare le statistiche');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { carica(); }, [carica]);

  const coinvolti = (stats?.perEsito || [])
    .filter((e) => e._id === 'richiamo' || e._id === 'stop_drive')
    .reduce((s, e) => s + e.n, 0);
  const maxGiorno = Math.max(...(stats?.perGiorno || []).map((g) => g.n), 0);
  const maxEsito = Math.max(...(stats?.perEsito || []).map((g) => g.n), 0);
  const maxMarca = Math.max(...(stats?.perMarca || []).map((g) => g.n), 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="max-w-5xl mx-auto px-4 space-y-4">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><ArrowLeft className="w-5 h-5" /></Link>
          <BarChart3 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <h1 className="text-base font-bold text-gray-900 dark:text-white">Statistiche verifica Takata</h1>
          <button onClick={carica} disabled={loading} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-400 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
          </button>
        </div>

        {!stats && loading && <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento…</p>}

        {stats && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <Tile icona={<BarChart3 className="w-4 h-4" />} valore={stats.totale.toLocaleString('it-IT')} etichetta="Verifiche totali" nota={`${stats.vinUnici.toLocaleString('it-IT')} telai unici`} />
              <Tile icona={<Store className="w-4 h-4" />} valore={stats.studi.toLocaleString('it-IT')} etichetta="Studi attivi" nota="hanno registrato ≥ 1 verifica" />
              <Tile icona={<Shield className="w-4 h-4" />} valore={coinvolti.toLocaleString('it-IT')} etichetta="Veicoli coinvolti" nota="richiami + stop-drive intercettati" />
              <Tile icona={<Users className="w-4 h-4" />} valore={`${Math.round((stats.perModalita.find((m) => m._id === 'estensione')?.n || 0) / Math.max(stats.totale, 1) * 100)}%`} etichetta="Via estensione" nota="quota sul totale" />
              <Tile icona={<HardDrive className="w-4 h-4" />} valore={fmtByte(stats.disco.byte)} etichetta="Spazio allegati" nota={`${stats.disco.file.toLocaleString('it-IT')} schermate di prova`} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Verifiche al giorno (ultimi {stats.giorni} giorni)</h2>
              {stats.perGiorno.map((g) => (
                <RigaBarra key={g._id} etichetta={fmtData(g._id)} n={g.n} max={maxGiorno} />
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Esiti</h2>
                {stats.perEsito.map((e) => (
                  <RigaBarra key={e._id} etichetta={ESITO_LABEL[e._id] || e._id} n={e.n} max={maxEsito} colore="bg-gray-300 dark:bg-gray-600" dot={ESITO_DOT[e._id] || 'bg-gray-400'} />
                ))}
                <h2 className="text-sm font-bold text-gray-900 dark:text-white mt-4 mb-2">Come verificano</h2>
                {stats.perModalita.map((m) => (
                  <div key={m._id} className="flex justify-between text-xs py-0.5">
                    <span className="text-gray-600 dark:text-gray-300">{MODALITA_LABEL[m._id] || m._id}</span>
                    <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{m.n.toLocaleString('it-IT')}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Marche più verificate</h2>
                {stats.perMarca.map((m) => (
                  <RigaBarra key={m._id} etichetta={m._id ? m._id.charAt(0).toUpperCase() + m._id.slice(1) : '(senza marca)'} n={m.n} max={maxMarca} />
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 overflow-x-auto">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Studi più attivi</h2>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="py-1 pr-2 font-medium">#</th>
                    <th className="py-1 pr-2 font-medium">Studio</th>
                    <th className="py-1 pr-2 font-medium">Prov.</th>
                    <th className="py-1 pr-2 font-medium text-right">Verifiche</th>
                    <th className="py-1 font-medium text-right">Ultima</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topStudi.map((s, i) => (
                    <tr key={`${s.studio}-${i}`} className="border-t border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                      <td className="py-1 pr-2 text-gray-400">{i + 1}</td>
                      <td className="py-1 pr-2">{s.studio}</td>
                      <td className="py-1 pr-2">{s.provincia || '—'}</td>
                      <td className="py-1 pr-2 text-right font-semibold text-gray-900 dark:text-white tabular-nums">{s.n.toLocaleString('it-IT')}</td>
                      <td className="py-1 text-right">{new Date(s.ultima).toLocaleDateString('it-IT')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TakataStatsAdmin;
